/** One resolved frame from an Error.stack or React componentStack string. */
export interface StackFrame {
  name: string;
  file: string;
  line: number;
  column: number;
}

/**
 * Pulls clickable frames out of an `Error.stack` / React `componentStack`.
 *
 * Only frames whose URL points at `/src/...` survive, so a production stack —
 * minified to hashed asset paths — yields an empty list. That makes any UI
 * built on this a dev-only affordance that degrades to nothing in prod rather
 * than needing its own environment check.
 *
 * Shared because both app error boundaries parse stacks identically, and the
 * regex is fiddly enough that two copies would drift.
 */
export function parseStackFrames(stack: string): StackFrame[] {
  const frames: StackFrame[] = [];
  for (const rawLine of stack.split("\n")) {
    const trimmed = rawLine.trim();
    // Browser format: "at Name (http://host/src/path.tsx?t=...:42:8)"
    const match = trimmed.match(
      /at\s+(\S+)\s+\(https?:\/\/[^/]+\/(.*?)(?:\?[^:]*)?:(\d+):(\d+)\)/,
    );
    if (match) {
      frames.push({
        name: match[1] ?? "",
        file: match[2] ?? "",
        line: parseInt(match[3] ?? "0", 10),
        column: parseInt(match[4] ?? "0", 10),
      });
    }
  }
  return frames.filter(
    (f) => !f.file.includes("node_modules") && f.file.includes("src/"),
  );
}
